// A player's wallet, moved (spec/13 §8, F-WAL-02/-06/-07): **Top up**, **Adjust** and **Refund**, offered on the
// player's Wallet tab at a venue — the money is taken or paid out at a counter, so each names the venue it happens
// at — each behind its own permission (`wallet:topup`, `wallet:adjust`, `wallet:refund`), which the server checks
// again. Each form opens as a step in place on the tab — above the balance it changes, never a dialog on the player
// dialog (2026-10-07) — and says what it did when it is done. These were the Wallet screen's until it folded into
// Players (2026-10-07); the endpoints and copy are the same.
//
// Where the venue's online gates are open (spec/06 §9.5), a top-up also offers **Online (UPI)** (F-PAY-03): it asks
// the payment provider for a request, shows it, and polls it — the wallet is credited by the server when the
// provider confirms, never by this screen.
//
// A refusal says why, in the server's terms (2026-10-12): each is matched on the catalogue's snake_case code
// (`PROBLEM`, checked against the contract's list) — the wallet off, an amount outside the limits (in the server's own
// words, which name the rupees), cash with no shift open, too little cash, an adjustment over the role's cap, refunds
// turned off — with a link to the setting it is about for someone who may change it. Cash locks, as at Sell, while the
// member holds no open drawer (ADR-0031), so the default tender is one the server will take.

import type { PaymentIntent, WalletAdjustRequest, WalletRefundRequest, WalletRefundTender, WalletTender, WalletTopUpRequest } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, cx, SelectField, TextField } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useCallback, useState, type FormEvent, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem, type ProblemReader } from '../../api/use-problem';
import { Icon } from '../../shell/nav-icons';
import { useConsoleScope } from '../../shell/scope';
import { Notice, Section, useStepFocus } from '../../ui';
import { moneyFrom } from '../money';
import { OnlinePayment } from '../payments/OnlinePayment';
import { useBeginOnlineTopUp, useOnlinePayments } from '../payments/queries';
import { CashLockedNotice, useCashLocked } from '../shifts/shift-prompt';
import { useAdjustWallet, useRefundWallet, useTopUpWallet, walletKeys } from '../wallet/queries';

const WALLET_TOPUP = 'wallet:topup';
const WALLET_ADJUST = 'wallet:adjust';
const WALLET_REFUND = 'wallet:refund';
/** The wallet's settings (`wallet.enabled`, its limits, refunds) are edited with `settings:payments`. */
const SETTINGS_PAYMENTS = 'settings:payments';

/** Where the money moves: the venue the member is at, and its currency. */
export interface WalletVenue {
  readonly organizationId: string;
  readonly venueId: string;
  readonly currency: string;
}

interface WalletPlayer {
  readonly id: string;
  readonly name: string;
}

type WalletAction = 'topUp' | 'adjust' | 'refund';

/**
 * The Wallet tab's actions at a venue: a button per action the role holds, each opening its form as a step in place
 * of the buttons — a titled panel with Cancel — until it is done or cancelled.
 */
export function WalletActions({ player, venue }: { readonly player: WalletPlayer; readonly venue: WalletVenue }): ReactNode {
  const { t } = useTranslation('console');
  const access = useAccess({ venueId: venue.venueId });
  const ready = access.status === 'ready';
  const canTopUp = ready && access.can(WALLET_TOPUP);
  const canAdjust = ready && access.can(WALLET_ADJUST);
  const canRefund = ready && access.can(WALLET_REFUND);
  // Which form is open in place, or none. The button that opened it takes focus back when it closes.
  const [kind, setKind] = useState<WalletAction | null>(null);
  const [last, setLast] = useState<WalletAction>('topUp');
  const { stepRef, opener } = useStepFocus(kind !== null);
  /** The button the open step came from carries the opener mark, so focus goes back to it. */
  const markIf = (action: WalletAction) => (last === action ? opener : {});
  const show = (next: WalletAction) => {
    setKind(next);
    setLast(next);
  };
  const close = () => setKind(null);

  if (!canTopUp && !canAdjust && !canRefund) return null;

  if (kind !== null) {
    return (
      <div ref={stepRef}>
        <Section
          title={t(`wallet.${kind}.dialogTitle`)}
          description={t(`wallet.${kind}.subtitle`, { name: player.name })}
          actions={
            <Button variant="ghost" size="sm" onClick={close}>
              {t('common.cancel')}
            </Button>
          }
        >
          {kind === 'topUp' ? <TopUpForm player={player} venue={venue} onClose={close} /> : null}
          {kind === 'adjust' ? <AdjustForm player={player} venue={venue} onClose={close} /> : null}
          {kind === 'refund' ? <RefundForm player={player} venue={venue} onClose={close} /> : null}
        </Section>
      </div>
    );
  }

  return (
    <div className="console-people-wallet-actions" role="group" aria-label={t('players.wallet.actions')}>
      {canTopUp ? (
        <Button {...markIf('topUp')} variant="primary" size="sm" icon={<Icon name="plus" />} onClick={() => show('topUp')}>
          {t('wallet.topUp.action')}
        </Button>
      ) : null}
      {canAdjust ? (
        <Button {...markIf('adjust')} variant="secondary" size="sm" icon={<Icon name="wrench" />} onClick={() => show('adjust')}>
          {t('wallet.adjust.action')}
        </Button>
      ) : null}
      {canRefund ? (
        <Button {...markIf('refund')} variant="secondary" size="sm" icon={<Icon name="arrowRight" />} onClick={() => show('refund')}>
          {t('wallet.refund.action')}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Whether this member may change the organisation's wallet settings, and its settings link for one key: the Money
 * screen at the organisation (where `wallet.enabled` and the limits are set), opened at the key. `undefined` for a
 * member who may not, or who belongs to the venue only.
 */
export function useWalletSettingsLink(): ((key: string, label: string) => ReactNode) | undefined {
  const scope = useConsoleScope();
  const access = useAccess(scope.kind === 'venue' ? { venueId: scope.venue.id } : { organizationId: scope.organization.id });
  const orgSlug = scope.organization?.slug;
  if (orgSlug === undefined || access.status !== 'ready' || !access.can(SETTINGS_PAYMENTS)) return undefined;
  return (key, label) => (
    <Button variant="secondary" size="sm" asChild>
      <Link to="/o/$orgSlug/settings/$group" params={{ orgSlug, group: 'money' }} hash={key}>
        {label}
      </Link>
    </Button>
  );
}

/** Why a top-up, an adjustment or a refund was refused, in words — and the setting it is about, when there is one. */
interface Refusal {
  readonly message: string;
  readonly setting?: string;
}

/** The setting a refusal is about, for someone who may change it (a link beside the message). */
const SETTING_FOR_CODE: Readonly<Record<string, string>> = {
  [PROBLEM.walletDisabled]: 'wallet.enabled',
  [PROBLEM.walletLimit]: 'wallet.minTopupAmount',
  [PROBLEM.walletTopupTaxAtSale]: 'tax.walletTopupTaxPoint',
  [PROBLEM.walletRefundsOff]: 'wallet.cashRefundsAllowed',
};

/**
 * A refusal in words: the catalogue's copy for its code (the server's own detail where that names the rupees, and the
 * field messages of a validation failure), with the setting it is about; the action's plain line for a code with no copy.
 */
function refusalOf(error: unknown, problem: ProblemReader, fallback: string): Refusal {
  const view = problem.view(error, { fallback });
  const code = problemCode(error);
  const setting = code === undefined ? undefined : SETTING_FOR_CODE[code];
  const message = view.isValidation && view.fields.length > 0 ? view.fields.map((entry) => entry.message).join(' ') : problem.text(error, { fallback });
  return setting === undefined ? { message } : { message, setting };
}

/** A refusal, said where the form is, with the setting it is about for someone who may change it. */
function RefusalNotice({ refusal }: { readonly refusal: Refusal }): ReactNode {
  const { t } = useTranslation('console');
  const settingsLink = useWalletSettingsLink();
  return (
    <Notice tone="danger" role="alert" action={refusal.setting !== undefined && settingsLink !== undefined ? settingsLink(refusal.setting, t('wallet.settingsLink')) : undefined}>
      {refusal.message}
    </Notice>
  );
}

/** A form's last step: what it did, and Done. */
function Done({ badge, children, close, onClose }: { readonly badge: string; readonly children: ReactNode; readonly close: string; readonly onClose: () => void }): ReactNode {
  return (
    <div className="console-form">
      <Notice tone="ok" role="status" title={badge}>
        {children}
      </Notice>
      <Button variant="primary" block onClick={onClose}>
        {close}
      </Button>
    </div>
  );
}

/** The counter's tenders; `online` is added where the venue's online gates are open (F-PAY-03). */
const COUNTER_TENDERS: readonly WalletTender[] = ['cash', 'counter_card', 'counter_upi'];
const TENDER_ICON: Readonly<Record<WalletTender, string>> = { cash: 'cash', counter_card: 'card', counter_upi: 'qr', online: 'bolt' };

interface FormProps {
  readonly player: WalletPlayer;
  readonly venue: WalletVenue;
  readonly onClose: () => void;
}

/** Top up at the counter (F-WAL-02), paid by a tender — or online, by UPI, where the venue takes it. */
function TopUpForm({ player, venue, onClose }: FormProps): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const queryClient = useQueryClient();
  const problem = useProblem();
  const topUp = useTopUpWallet(venue.organizationId, venue.venueId);
  const beginOnline = useBeginOnlineTopUp(venue.organizationId, venue.venueId);
  const onlinePayments = useOnlinePayments(venue.organizationId, venue.venueId);
  const tenders: readonly WalletTender[] = onlinePayments.data?.enabled === true ? [...COUNTER_TENDERS, 'online'] : COUNTER_TENDERS;
  // ADR-0031, as at Sell: only cash needs a shift — locked for a member holding no open drawer, who is asked to start one.
  const cashLocked = useCashLocked({ id: venue.venueId, organizationId: venue.organizationId });

  const [amount, setAmount] = useState('');
  const [chosenTender, setTender] = useState<WalletTender>('cash');
  // The day's status may arrive after the form opened: a Cash tender that has just locked moves to counter UPI.
  const tender: WalletTender = cashLocked && chosenTender === 'cash' ? 'counter_upi' : chosenTender;
  const [error, setError] = useState<Refusal | undefined>(undefined);
  const [done, setDone] = useState<{ readonly amount: string; readonly total: string } | null>(null);
  /** The payment request the player is paying now, for an Online top-up. */
  const [intent, setIntent] = useState<PaymentIntent | null>(null);
  const [onlineDone, setOnlineDone] = useState<string | null>(null);

  // The provider answered. A capture credited the wallet on the server, so its balance and statement are read
  // again; anything else leaves the wallet as it was.
  const onIntentSettled = useCallback(
    (settled: PaymentIntent) => {
      if (settled.status !== 'succeeded') return;
      void queryClient.invalidateQueries({ queryKey: walletKeys.balance(venue.organizationId, player.id) });
      void queryClient.invalidateQueries({ queryKey: walletKeys.statement(venue.organizationId, player.id) });
      setOnlineDone(format.money(settled.capturedAmount ?? settled.amount));
    },
    [queryClient, venue.organizationId, player.id, format],
  );

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const amountMoney = moneyFrom(amount, venue.currency);
    if (amountMoney === null || amountMoney.amount <= 0) {
      setError({ message: t('wallet.topUp.amountInvalid') });
      return;
    }
    setError(undefined);
    if (tender === 'online') {
      beginOnline.mutate(
        { playerId: player.id, amount: amountMoney },
        {
          onSuccess: setIntent,
          // Anything the wallet does not refuse by name is the payment request's own failure.
          onError: (mutationError) => setError(refusalOf(mutationError, problem, t('onlinePayment.refused'))),
        },
      );
      return;
    }
    const body: WalletTopUpRequest = { amount: amountMoney, tender };
    topUp.mutate(
      { playerId: player.id, body },
      {
        onSuccess: (result) => setDone({ amount: format.money(result.topUp.cashAmount), total: format.money(result.wallet.total) }),
        onError: (mutationError) => setError(refusalOf(mutationError, problem, t('wallet.topUp.refused'))),
      },
    );
  }

  if (done !== null || onlineDone !== null) {
    return (
      <Done badge={t('wallet.topUp.doneBadge')} close={t('wallet.topUp.close')} onClose={onClose}>
        {done !== null ? t('wallet.topUp.done', { amount: done.amount, total: done.total }) : t('wallet.topUp.onlineDone', { amount: onlineDone })}
      </Done>
    );
  }

  if (intent !== null) {
    return <OnlinePayment organizationId={venue.organizationId} venueId={venue.venueId} intent={intent} onSettled={onIntentSettled} onAgain={() => setIntent(null)} />;
  }

  return (
    <form className="console-form" aria-label={t('wallet.topUp.title', { name: player.name })} noValidate onSubmit={submit}>
      {error !== undefined ? <RefusalNotice refusal={error} /> : null}
      <TextField label={t('wallet.topUp.amount', { currency: venue.currency })} required numeric inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
      <fieldset className="console-tenders">
        <legend className="clav-field__label">{t('wallet.topUp.tender')}</legend>
        <div className={cx('console-tenders__grid', tenders.length === 3 && 'console-tenders__grid--three')}>
          {tenders.map((value) => {
            const locked = value === 'cash' && cashLocked;
            return (
              <label key={value} className={cx('console-tenders__option', tender === value && 'is-checked', locked && 'is-locked')}>
                <input
                  type="radio"
                  className="clav-visually-hidden"
                  name="wallet-tender"
                  value={value}
                  checked={tender === value}
                  disabled={locked}
                  onChange={() => setTender(value)}
                />
                <span className="console-tenders__icon" aria-hidden="true">
                  <Icon name={TENDER_ICON[value]} />
                </span>
                <span>{t(`wallet.tender.${value}`)}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
      {cashLocked ? <CashLockedNotice /> : null}
      {tender === 'online' ? <p className="console-subtle">{t('wallet.topUp.onlineHint')}</p> : null}
      <Button type="submit" variant="primary" size="lg" block busy={topUp.isPending || beginOnline.isPending}>
        {tender === 'online' ? t('wallet.topUp.requestOnline') : t('wallet.topUp.submit')}
      </Button>
    </form>
  );
}

const ADJUST_DIRECTIONS: readonly WalletAdjustRequest['direction'][] = ['credit', 'debit'];

/** Staff credit or debit a player's wallet cash with a reason (F-WAL-07). The server caps the amount and audits it. */
function AdjustForm({ player, venue, onClose }: FormProps): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const adjust = useAdjustWallet(venue.organizationId, venue.venueId);

  const [direction, setDirection] = useState<WalletAdjustRequest['direction']>('credit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<Refusal | undefined>(undefined);
  const [done, setDone] = useState<{ readonly total: string } | null>(null);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const amountMoney = moneyFrom(amount, venue.currency);
    if (amountMoney === null || amountMoney.amount <= 0) {
      setError({ message: t('wallet.adjust.amountInvalid') });
      return;
    }
    if (reason.trim() === '') {
      setError({ message: t('wallet.adjust.reasonRequired') });
      return;
    }
    setError(undefined);
    const body: WalletAdjustRequest = { direction, amount: amountMoney, reason: reason.trim() };
    adjust.mutate(
      { playerId: player.id, body },
      {
        onSuccess: (result) => setDone({ total: format.money(result.wallet.total) }),
        onError: (mutationError) => setError(refusalOf(mutationError, problem, t('wallet.adjust.refused'))),
      },
    );
  }

  if (done !== null) {
    return (
      <Done badge={t('wallet.adjust.doneBadge')} close={t('wallet.adjust.close')} onClose={onClose}>
        {t('wallet.adjust.done', { total: done.total })}
      </Done>
    );
  }

  return (
    <form className="console-form" aria-label={t('wallet.adjust.title', { name: player.name })} noValidate onSubmit={submit}>
      {error !== undefined ? <RefusalNotice refusal={error} /> : null}
      <SelectField
        label={t('wallet.adjust.direction')}
        required
        options={ADJUST_DIRECTIONS.map((value) => ({ value, label: t(`wallet.adjust.dir.${value}`) }))}
        value={direction}
        onValueChange={(value) => setDirection(value as WalletAdjustRequest['direction'])}
      />
      <TextField label={t('wallet.adjust.amount', { currency: venue.currency })} required numeric inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
      <TextField label={t('wallet.adjust.reason')} required value={reason} onChange={(event) => setReason(event.target.value)} />
      <Button type="submit" variant="primary" size="lg" block busy={adjust.isPending}>
        {t('wallet.adjust.submit')}
      </Button>
    </form>
  );
}

const REFUND_TENDERS: readonly WalletRefundTender[] = ['cash', 'counter_card', 'counter_upi'];

/** Refund a player's wallet cash to a counter tender with a reason (F-WAL-06). Cash only — never the bonus balance. */
function RefundForm({ player, venue, onClose }: FormProps): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const refund = useRefundWallet(venue.organizationId, venue.venueId);

  const [amount, setAmount] = useState('');
  const [tender, setTender] = useState<WalletRefundTender>('cash');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<Refusal | undefined>(undefined);
  const [done, setDone] = useState<{ readonly total: string } | null>(null);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const amountMoney = moneyFrom(amount, venue.currency);
    if (amountMoney === null || amountMoney.amount <= 0) {
      setError({ message: t('wallet.refund.amountInvalid') });
      return;
    }
    if (reason.trim() === '') {
      setError({ message: t('wallet.refund.reasonRequired') });
      return;
    }
    setError(undefined);
    const body: WalletRefundRequest = { amount: amountMoney, tender, reason: reason.trim() };
    refund.mutate(
      { playerId: player.id, body },
      {
        onSuccess: (result) => setDone({ total: format.money(result.wallet.total) }),
        onError: (mutationError) => setError(refusalOf(mutationError, problem, t('wallet.refund.refused'))),
      },
    );
  }

  if (done !== null) {
    return (
      <Done badge={t('wallet.refund.doneBadge')} close={t('wallet.refund.close')} onClose={onClose}>
        {t('wallet.refund.done', { total: done.total })}
      </Done>
    );
  }

  return (
    <form className="console-form" aria-label={t('wallet.refund.title', { name: player.name })} noValidate onSubmit={submit}>
      {error !== undefined ? <RefusalNotice refusal={error} /> : null}
      <TextField label={t('wallet.refund.amount', { currency: venue.currency })} required numeric inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
      <SelectField
        label={t('wallet.refund.tender')}
        required
        options={REFUND_TENDERS.map((value) => ({ value, label: t(`wallet.tender.${value}`) }))}
        value={tender}
        onValueChange={(value) => setTender(value as WalletRefundTender)}
      />
      <TextField label={t('wallet.refund.reason')} required value={reason} onChange={(event) => setReason(event.target.value)} />
      <Button type="submit" variant="primary" size="lg" block busy={refund.isPending}>
        {t('wallet.refund.submit')}
      </Button>
    </form>
  );
}
